import Vue from "vue";
import deepmerge from "deepmerge";
import set from "lodash/set";
import get from "lodash/get";
import {
  _isMobile,
  typologyTagging,
  bodyProductAttributes
} from "@/utils/helpers";

const enviroments = [
  {
    name: "pre",
    description: "Pre-produccion",
    host: /desainternet70.arg.igrupobbva/
  },
  {
    name: "prod",
    description: "Produccion",
    host: /bbva.com.ar/
  },
  {
    name: "de",
    description: "Desarrollo",
    host: /desa1-qa.bbvafrances.com.ar/
  },
  {
    name: "de",
    description: "Desarrollo",
    host: /desa2-qa.bbvafrances.com.ar/
  },
  {
    name: "de",
    description: "Desarrollo",
    host: /desa2-qa.bbva.com.ar/
  },
  {
    name: "de",
    description: "Desarrollo",
    host: /localhost/
  },
  {
    name: "qa",
    description: "Calidad",
    host: /was70internettest1/
  }
];

/**
 * Configuración del circuito en Adobe
 * (application flow description)
 */
const applicationConfig = {
  ofertaHipotecarios: {
    application: {
      type: "simulador",
      name: "simulador hipotecario"
    },
    fulfillmentModel: "online",
    globalApplication: "mortgage simulator"
  },
  ofertaPersonales: {
    application: {
      type: "contratacion",
      name: "prestamo open market"
    },
    fulfillmentModel: "online/offline",
    globalApplication: "onboarding loan"
  },
  ofertaPrestamosEmprendedores: {
    application: {
      type: "contratacion",
      name: "prestamo open market"
    },
    fulfillmentModel: "online/offline",
    globalApplication: "onboarding loan"
  }
};

export const applicationProps = applicationConfig[_BBVA_MOTOR];

const _normalizeCharacters = str => {
  return str
    ? (str + "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
    : str;
};

const _calculatePayments = val => {
  return val !== null || val !== "" ? String(Number(val) * 12) : null;
};

const AdobeFunctions = {
  internal(context, _callback) {
    AdobeFunctions.clearVars();
    AdobeFunctions.setConstants();
    AdobeFunctions.setUserVars(context);
    AdobeFunctions.setOptimizations(context);
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event) {
    /**
     * Realizar llamada al DTM
     */
    if (event !== "Page View" && event !== "Internal Link") {
      // No se debe reportar los props de application para page views
      AdobeFunctions.setApplicationProps();
    }
    try {
      window[method](event);
      // Resetear flag de pageview automático
      window._pageViewAutomatico = false;
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "internalCampaign.attributes", []);
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(window.digitalData, "application.errorType", "");
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(window.digitalData, "page.pageActivity.link.name", "");
    set(window.digitalData, "application.typology", "");
    set(window.digitalData, "application.process", "");
    set(window.digitalData, "application.operationNumber", "");
    set(window.digitalData, "application.customFields", "");
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
    set(window.digitalData, "user.segment.profile", "");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: ""
        },
        fulfillmentModel: "",
        globalApplication: ""
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    let host = window.location.hostname;
    for (let i = 0; i < enviroments.length; i++) {
      if (host.match(enviroments[i].host) !== null) {
        set(window.digitalData, "pageInstanceID", enviroments[i].name);
        return;
      }
    }
    set(window.digitalData, "pageInstanceID", "pro");
  },
  setIsMobile() {
    if (_isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (_isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      _BBVA_MOTOR === "ofertaHipotecarios" ? "personas" : "general",
      _BBVA_MOTOR === "ofertaHipotecarios"
        ? "simulador hipotecario"
        : "prestamo open market"
    ];
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level2", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");
    AdobeFunctions.setPageInstanceId();
    AdobeFunctions.setIsMobile();
    AdobeFunctions.setSysEnv();
  },
  setOptimizations(context) {
    if (context && context.state && context.state.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej: "AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: ""
          }
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setInternalCampaign() {
    let attributes = [
      {
        location: "",
        campaignFormat: "",
        collectiveCode: "",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: ""
      }
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setProductAttributes(context, contratacion) {
    let attributes = [];
    let attributesModel = {
      primaryCategory: "",
      productSubtype: "",
      productName: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: ""
      },
      term: "",
      group: "",
      state: ""
    };

    const __addProduct = producto => {
      attributes.push(
        deepmerge(attributesModel, {
          primaryCategory: get(producto, "primaryCategory", ""),
          productSubtype: get(producto, "productSubtype", ""),
          productName: get(producto, "productName", ""),
          numberOfPayments: get(producto, "numberOfPayments", ""),
          amount: get(producto, "amount", ""),
          currency: get(producto, "currency", ""),
          paymentAmount: get(producto, "paymentAmount")
        })
      );
    };
    contratacion =
      _BBVA_MOTOR === "ofertaHipotecarios" ? "hipotecario" : contratacion;
    __addProduct(bodyProductAttributes(contratacion, context));
    if (contratacion === "contratacion") {
      __addProduct(bodyProductAttributes("cuentas", context));
      __addProduct(bodyProductAttributes("tarjetas", context));
    }
    set(window.digitalData, "products.attributes", attributes);
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps() {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, applicationProps)
    );
  },
  setCustomFields(context) {
    if (_BBVA_MOTOR !== "ofertaHipotecarios") {
      let customFields = null;
      let customerID = "";
      if (get(context, "state.esCliente") === true) {
        customFields = "usuario:cliente";
        customerID = get(
          context,
          "state.simulacion.resultado.clientePrestamo.idAltamira",
          ""
        );
      } else {
        customFields = "usuario:no cliente";
      }
      set(
        window.digitalData,
        "application",
        deepmerge(window.digitalData.application, { customFields, customerID })
      );
    }
  },
  setHipotecariosDefaults() {
    set(window.digitalData, "page.pageInfo.pageSegment", "personas");
    set(window.digitalData, "page.pageInfo.pageIntent", "informacion");

    set(
      window.digitalData,
      "application.application.name",
      "simulador hipotecario"
    );

    set(
      window.digitalData,
      "products.attributes[0].productSubtype",
      "prestamos hipotecarios"
    );

    set(
      window.digitalData,
      "products.attributes[0].primaryCategory",
      "prestamos"
    );
  },
  setTypology() {
    if (_BBVA_MOTOR !== "ofertaHipotecarios") {
      const ofertaTypology = typologyTagging();
      set(window.digitalData, "application.typology", ofertaTypology);
    }
  },
  setUserVars(context) {
    set(
      window.digitalData,
      "user.prospectID",
      get(context, "state.userIdTokenizado", "")
    );
  }
};

export { AdobeFunctions, _normalizeCharacters, _calculatePayments };
